import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const CourseView = ({ user, onLogout }) => {
  const [course, setCourse] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [searchParams] = useSearchParams();
  const moduleId = searchParams.get('module');

  useEffect(() => {
    fetchCourse();
  }, []);

  useEffect(() => {
    if (course && moduleId) {
      const module = course.modules.find(m => m.id === moduleId);
      if (module) {
        setSelectedModule(module);
      }
    } else if (course && course.modules.length > 0) {
      // Default to first unlocked module
      const firstUnlocked = course.modules.find(m => !m.is_locked);
      if (firstUnlocked) {
        setSelectedModule(firstUnlocked);
      }
    }
  }, [course, moduleId]);

  const fetchCourse = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/course`);
      const courseData = await response.json();
      setCourse(courseData);
    } catch (error) {
      console.error('Error fetching course:', error);
    }
  };

  const isModuleAccessible = (module) => {
    return user?.has_sbt || user?.subscription_status === 'enrolled' || !module.is_locked;
  };

  const getModuleContent = (module) => {
    if (!module) return '';
    
    // Mock content for demonstration
    const contents = {
      '1': `
        <h3>Bem-vindo ao Direito Digital</h3>
        <p>Neste módulo introdutório, você aprenderá os fundamentos do direito na era digital.</p>
        
        <h4>Tópicos Abordados:</h4>
        <ul>
          <li>História e evolução do direito digital</li>
          <li>Marco Civil da Internet no Brasil</li>
          <li>LGPD e proteção de dados pessoais</li>
          <li>Contratos eletrônicos e assinatura digital</li>
        </ul>

        <h4>Vídeo Aula:</h4>
        <div style="background: #1a1a2e; padding: 2rem; border-radius: 8px; text-align: center; margin: 1rem 0;">
          <p style="color: #00ffff; margin: 0;">🎥 Introdução ao Direito Digital</p>
          <p style="opacity: 0.7; margin: 0.5rem 0;">Duração: 45 minutos</p>
          <button class="btn-primary" style="margin-top: 1rem;">▶ Reproduzir Vídeo</button>
        </div>

        <h4>Materiais Complementares:</h4>
        <ul>
          <li>📄 <a href="#" style="color: #00ffff;">Marco Civil da Internet - Lei 12.965/2014</a></li>
          <li>📄 <a href="#" style="color: #00ffff;">LGPD - Lei 13.709/2018</a></li>
          <li>📚 <a href="#" style="color: #00ffff;">Livro: "Direito Digital" - Gilmara Nagurnhak</a></li>
        </ul>
      `,
      '2': `
        <h3>Blockchain e Marco Legal</h3>
        <p>Entenda a tecnologia blockchain e suas implicações no ordenamento jurídico brasileiro.</p>
        
        <h4>Conteúdo Exclusivo para Matriculados</h4>
        <p>Para acessar este módulo, você precisa estar matriculado no curso.</p>
        <div style="background: rgba(255, 107, 53, 0.2); padding: 2rem; border-radius: 8px; text-align: center; margin: 2rem 0; border: 1px solid #ff6b35;">
          <p style="color: #ff6b35; margin: 0; font-weight: bold;">🔒 Conteúdo Bloqueado</p>
          <p style="opacity: 0.8; margin: 1rem 0;">Matricule-se para acessar este e outros módulos exclusivos.</p>
          <button class="btn-primary" onclick="window.location.href='/dashboard'">Matricular-se Agora</button>
        </div>
      `,
      '3': `
        <h3>Criptomoedas e Tributação</h3>
        <p>Aspectos tributários das criptomoedas no Brasil e compliance fiscal.</p>
        <div style="background: rgba(255, 107, 53, 0.2); padding: 2rem; border-radius: 8px; text-align: center; margin: 2rem 0; border: 1px solid #ff6b35;">
          <p style="color: #ff6b35; margin: 0; font-weight: bold;">🔒 Conteúdo Bloqueado</p>
          <p style="opacity: 0.8; margin: 1rem 0;">Matricule-se para acessar este e outros módulos exclusivos.</p>
        </div>
      `,
      '4': `
        <h3>Smart Contracts e Compliance</h3>
        <p>Contratos inteligentes e conformidade regulatória no ecossistema Web3.</p>
        <div style="background: rgba(255, 107, 53, 0.2); padding: 2rem; border-radius: 8px; text-align: center; margin: 2rem 0; border: 1px solid #ff6b35;">
          <p style="color: #ff6b35; margin: 0; font-weight: bold;">🔒 Conteúdo Bloqueado</p>
          <p style="opacity: 0.8; margin: 1rem 0;">Matricule-se para acessar este e outros módulos exclusivos.</p>
        </div>
      `
    };

    return contents[module.id] || '<p>Conteúdo não encontrado.</p>';
  };

  return (
    <div className="course-view">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-content">
          <div className="logo" onClick={() => window.location.href = '/'} style={{ cursor: 'pointer' }}>
            Habeas Cripto
          </div>
          <div className="nav-actions">
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <span style={{ color: 'var(--silver-mist)' }}>Olá, {user.name}</span>
              <button className="btn-ghost" onClick={() => window.location.href = '/dashboard'} data-testid="dashboard-btn">
                Dashboard
              </button>
              <button className="btn-ghost" onClick={onLogout} data-testid="logout-btn">
                Sair
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div style={{ display: 'flex', minHeight: '100vh', paddingTop: '80px' }}>
        {/* Sidebar - Module List */}
        <div style={{
          width: '350px',
          background: 'rgba(26, 26, 46, 0.95)',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '2rem 1rem',
          overflow: 'auto'
        }}>
          <h3 style={{ color: 'var(--cyber-blue)', marginBottom: '2rem', padding: '0 1rem' }} data-testid="modules-sidebar-title">
            Módulos do Curso
          </h3>
          
          {course?.modules.map((module) => {
            const isAccessible = isModuleAccessible(module);
            const isSelected = selectedModule?.id === module.id;
            
            return (
              <div
                key={module.id}
                className="glass-card"
                style={{
                  padding: '1.5rem',
                  marginBottom: '1rem',
                  cursor: isAccessible ? 'pointer' : 'not-allowed',
                  opacity: isAccessible ? 1 : 0.6,
                  border: isSelected ? '2px solid var(--cyber-blue)' : '1px solid rgba(255, 255, 255, 0.1)',
                  background: isSelected ? 'rgba(0, 255, 255, 0.1)' : 'rgba(26, 26, 46, 0.25)'
                }}
                onClick={() => isAccessible && setSelectedModule(module)}
                data-testid={`module-sidebar-${module.id}`}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <h5 style={{ color: 'var(--neon-green)', margin: 0, fontSize: '0.9rem' }}>
                    Módulo {module.order}
                  </h5>
                  <span style={{ 
                    fontSize: '0.75rem',
                    color: isAccessible ? 'var(--neon-green)' : 'var(--cyber-orange)'
                  }}>
                    {isAccessible ? '✓' : '🔒'}
                  </span>
                </div>
                <h4 style={{ color: 'var(--silver-mist)', margin: 0, fontSize: '1rem', marginBottom: '0.5rem' }}>
                  {module.title}
                </h4>
                <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.7, lineHeight: '1.3' }}>
                  {module.description}
                </p>
                <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'var(--cyber-blue)' }}>
                  ⏱️ {module.duration_minutes} min
                </div>
              </div>
            );
          })}
        </div>

        {/* Main Content Area */}
        <div style={{ flex: 1, padding: '2rem', overflow: 'auto' }}>
          {selectedModule ? (
            <div>
              {/* Module Header */}
              <div className="glass-card" style={{ padding: '2rem', marginBottom: '2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div>
                    <h1 style={{ color: 'var(--cyber-blue)', marginBottom: '0.5rem' }} data-testid="selected-module-title">
                      Módulo {selectedModule.order}: {selectedModule.title}
                    </h1>
                    <p style={{ opacity: 0.8, marginBottom: '1rem' }}>
                      {selectedModule.description}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: 'var(--cyber-blue)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                      ⏱️ {selectedModule.duration_minutes} minutos
                    </div>
                    <span style={{ 
                      background: isModuleAccessible(selectedModule) ? 'rgba(0, 255, 136, 0.2)' : 'rgba(255, 107, 53, 0.2)',
                      color: isModuleAccessible(selectedModule) ? 'var(--neon-green)' : 'var(--cyber-orange)',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.875rem'
                    }}>
                      {isModuleAccessible(selectedModule) ? '✓ Liberado' : '🔒 Bloqueado'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Module Content */}
              <div className="glass-card" style={{ padding: '2.5rem' }} data-testid="module-content">
                {isModuleAccessible(selectedModule) ? (
                  <div 
                    dangerouslySetInnerHTML={{ __html: getModuleContent(selectedModule) }}
                    style={{ lineHeight: '1.8' }}
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
                    <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔒</div>
                    <h3 style={{ color: 'var(--cyber-orange)', marginBottom: '1rem' }}>
                      Conteúdo Bloqueado
                    </h3>
                    <p style={{ opacity: 0.8, marginBottom: '2rem', fontSize: '1.1rem' }}>
                      Para acessar este módulo, você precisa estar matriculado no curso Habeas Cripto.
                    </p>
                    <button 
                      className="btn-primary" 
                      onClick={() => window.location.href = '/dashboard'}
                      data-testid="enroll-from-course-btn"
                    >
                      Matricular-se Agora
                    </button>
                  </div>
                )}
              </div>

              {/* Navigation Between Modules */}
              {isModuleAccessible(selectedModule) && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
                  <button 
                    className="btn-ghost" 
                    onClick={() => {
                      const currentIndex = course.modules.findIndex(m => m.id === selectedModule.id);
                      if (currentIndex > 0) {
                        setSelectedModule(course.modules[currentIndex - 1]);
                      }
                    }}
                    disabled={course?.modules.findIndex(m => m.id === selectedModule.id) === 0}
                    data-testid="prev-module-btn"
                  >
                    ← Módulo Anterior
                  </button>
                  
                  <button 
                    className="btn-primary" 
                    onClick={() => {
                      const currentIndex = course.modules.findIndex(m => m.id === selectedModule.id);
                      if (currentIndex < course.modules.length - 1) {
                        const nextModule = course.modules[currentIndex + 1];
                        if (isModuleAccessible(nextModule)) {
                          setSelectedModule(nextModule);
                        }
                      }
                    }}
                    disabled={
                      !course || 
                      course.modules.findIndex(m => m.id === selectedModule.id) === course.modules.length - 1 ||
                      !isModuleAccessible(course.modules[course.modules.findIndex(m => m.id === selectedModule.id) + 1])
                    }
                    data-testid="next-module-btn"
                  >
                    Próximo Módulo →
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
              <h2 style={{ color: 'var(--cyber-blue)', marginBottom: '1rem' }}>
                Selecione um Módulo
              </h2>
              <p style={{ opacity: 0.8 }}>
                Escolha um módulo na barra lateral para começar seus estudos.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseView;